import { useNavigate } from "react-router-dom";
import { getArticle, deleteArticle, starArticle } from "./_request";
import React, { useEffect, useState, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import CircularProgress from '@mui/material/CircularProgress';

const Tazakuraat = () => {
    const navigate = useNavigate();
    const { authToken, themeMode } = useAuth();
    const isDark = themeMode === 'dark';
    const [articles, setArticles] = useState([]);
    const [loading, setLoading] = useState(false);

    const fetchArticles = useCallback(async () => {
        setLoading(true);
        try {
            const response = await getArticle(authToken);
            if (response.Result) {
                const data = response.Articles;
                setArticles(data);
            } else {
                console.error(response.Msg);
            }
        } catch (error) {
            console.error("Error fetching articles:", error);
        } finally {
            setLoading(false);
        }
    }, [authToken]);

    useEffect(() => {
        fetchArticles();
    }, [authToken, fetchArticles]);

    const handleDelete = async (id) => {
        const confirmDelete = window.confirm("Are you sure you want to delete this article?");
        if (confirmDelete) {
            try {
                const response = await deleteArticle(authToken, id);
                if (response.Result) {
                    alert("Article deleted successfully!");
                    setArticles((prevArticles) =>
                        prevArticles.filter((article) => article.id !== id)
                    );
                } else {
                    alert("Failed to delete the article.");
                }
            } catch (error) {
                console.error("Error deleting article:", error);
                alert("An error occurred while deleting the article.");
            }
        }
    };

    const handleEditClick = (articleId) => {
        navigate('/tazakuraat/editTazakraat', { state: { articleId } });
    };

    const handleStar = async (article) => {
        const isStarred = article.star === "Starred";
        const confirmToggle = window.confirm(
            isStarred
                ? "Do you want to remove the star from this article?"
                : "Do you want to mark this article as Starred?"
        );
        if (confirmToggle) {
            try {
                const response = await starArticle(authToken, article.id);
                if (response.Result) {
                    alert(isStarred ? "Star removed!" : "Article starred!");
                    fetchArticles();
                } else {
                    alert("Failed to update star status.");
                }
            } catch (error) {
                console.error("Error toggling star status:", error);
                alert("An error occurred while updating the article.");
            }
        }
    };

    return (
        <div className="space-y-6 pb-8">
            {/* Header */}
            <div className="glass-panel p-6 flex justify-between items-center">
                <div>
                    <h1 className={`font-extrabold text-2xl sm:text-3xl ${isDark ? 'text-white' : 'text-slate-900'}`}>Tazakuraat Articles</h1>
                    <p className={`text-xs mt-1 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Manage articles, categories, reading time, and starred content</p>
                </div>
                <button
                    onClick={() => navigate('/tazakuraat/addTazakraat')}
                    className="glass-btn-primary px-5 py-2.5 text-xs font-semibold"
                >
                    + Add Article
                </button>
            </div>

            {/* Articles Table */}
            <div className="glass-panel overflow-hidden">
                {loading ? (
                    <div className="flex flex-col items-center justify-center p-12 space-y-3">
                        <CircularProgress color="primary" />
                        <div className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Loading articles...</div>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full min-w-full glass-table">
                            <thead>
                                <tr>
                                    <th className="py-3.5 px-4 text-left font-semibold text-xs uppercase tracking-wider">ID</th>
                                    <th className="py-3.5 px-4 text-left font-semibold text-xs uppercase tracking-wider">Title</th>
                                    <th className="py-3.5 px-4 text-left font-semibold text-xs uppercase tracking-wider">Category</th>
                                    <th className="py-3.5 px-4 text-left font-semibold text-xs uppercase tracking-wider">Type</th>
                                    <th className="py-3.5 px-4 text-left font-semibold text-xs uppercase tracking-wider">Read Time</th>
                                    <th className="py-3.5 px-4 text-left font-semibold text-xs uppercase tracking-wider">Status</th>
                                    <th className="py-3.5 px-4 text-center font-semibold text-xs uppercase tracking-wider">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {articles.map((article) => (
                                    <tr key={article.id} className="transition-colors hover:bg-white/5">
                                        <td className="py-3.5 px-4 text-xs font-mono font-semibold">{article.id}</td>
                                        <td className="py-3.5 px-4 text-xs font-semibold max-w-xs truncate">{article.title}</td>
                                        <td className="py-3.5 px-4 text-xs font-medium">{article.category}</td>
                                        <td className="py-3.5 px-4 text-xs">{article.article_type}</td>
                                        <td className="py-3.5 px-4 text-xs font-mono">{article.read_min ? `${article.read_min} Min` : "0 Min"}</td>
                                        <td className="py-3.5 px-4 text-xs">
                                            <span className={`px-2.5 py-1 rounded-full text-[11px] font-bold ${
                                                article.status === 'Approved' ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30' : 'bg-amber-500/20 text-amber-400 border border-amber-500/30'
                                            }`}>
                                                {article.status || 'Pending'}
                                            </span>
                                        </td>
                                        <td className="py-3.5 px-4 text-center">
                                            <div className="flex items-center justify-center space-x-2">
                                                <button 
                                                    className="glass-btn-secondary px-3 py-1.5 text-xs font-medium"
                                                    onClick={() => handleEditClick(article.id)}
                                                >
                                                    Edit
                                                </button>
                                                <button 
                                                    className="glass-btn-danger px-3 py-1.5 text-xs font-medium"
                                                    onClick={() => handleDelete(article.id)}
                                                >
                                                    Delete
                                                </button>
                                                <button 
                                                    className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all border ${
                                                        article.star === "Starred" 
                                                            ? 'bg-amber-500/20 text-amber-300 border-amber-500/40 hover:bg-amber-500/30' 
                                                            : 'bg-slate-800/60 text-slate-300 border-slate-700 hover:bg-slate-700'
                                                    }`}
                                                    onClick={() => handleStar(article)}
                                                >
                                                    {article.star === "Starred" ? "★ Starred" : "☆ Star"}
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                                {articles.length === 0 && (
                                    <tr>
                                        <td colSpan="7" className="py-8 text-center text-sm text-slate-400">
                                            No articles found.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
};

export default Tazakuraat;
